import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { usePrevious } from '../../../hooks/usePrevious';
import {
    AgGridEvent,
    ClientSideRowModelModule,
    GridApi,
    ColumnResizedEvent,
    FilterChangedEvent,
    GridReadyEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { currentTranslations } from '../../../../core/locales/locale';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../state/ApplicationState';
import { TableButtonRenderer } from '../../../components/AgGrid/Renderers/TableRemoveButton/TableRemoveButton';
import {
    applyInitialSortFromDisplayPreferences,
    createTachoColumnState,
} from '../../../../core/domainModels/displayPreferences/tachoColumnState';
import AgGridContextMenuWrapper from '../../../components/ContextMenu/AgGridContextMenuWrapper';
import { modifyLicenseTermsDisplayPreferences, modifyLicenseTermsTableFilters } from '../../../../state/licenseTerms/reducer';
import { licenseTermsTableColumnsDefinitions, licenseTermsTableDefaultColumnsDefs } from './models/columnDefinitions';
import { LicenseTerm } from '../../../../core/domainModels/licenseTerms/LicenseTerm';

interface LicenseTermsTableProps {
    licenseTerms: Array<LicenseTerm>;
    onDoubleClicked: () => void;
    onSelectionChanged: (licenseTerms: Array<LicenseTerm>) => void;
}
export const LicenseTermsTable = (props: LicenseTermsTableProps) => {
    const dispatch = useDispatch();
    const displayPreferences = useSelector((state: ApplicationState) => state.licenseTerms.displayPreferences);
    const tableFiltersState = useSelector((state: ApplicationState) => state.licenseTerms.tableFilters);
    const { formatMessage } = useIntl();
    const gridApi = useRef<GridApi>();

    const columnDefs = useMemo(
        () => licenseTermsTableColumnsDefinitions(formatMessage, displayPreferences),
        [formatMessage, displayPreferences],
    );

    const previousLicenseTerms = usePrevious(props.licenseTerms);
    useEffect(() => {
        const added = props.licenseTerms.filter(term => !previousLicenseTerms?.find(prevTerm => prevTerm.id === term.id));
        const removed = previousLicenseTerms?.filter(prevTerm => !props.licenseTerms?.find(term => prevTerm.id === term.id));
        const updated = props.licenseTerms.filter(term => !added.some(addedTerm => addedTerm.id === term.id));
        gridApi.current?.applyTransaction({
            add: added,
            remove: removed,
            update: updated,
        });
    }, [props.licenseTerms]);

    const rowsSelectionChanged = (event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes()?.map(node => node.data as LicenseTerm);
        if (!selectedNodes) {
            console.log('No selected rows.');
            return;
        }

        props.onSelectionChanged(selectedNodes);
    };

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            gridApi.current = params.api;
            applyInitialSortFromDisplayPreferences(params, displayPreferences, [{ colId: 'startDate', sort: 'asc' }]);
            params.api.setFilterModel(tableFiltersState);
            params.api.setGridOption('rowData', props.licenseTerms);
        },
        [props.licenseTerms, displayPreferences, tableFiltersState],
    );

    const sideBarConfiguration = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivotMode: true,
                    suppressColumnFilter: true,
                    suppressColumnSelectAll: true,
                },
            },
        ],
    };

    function onColumnsChanged(event: AgGridEvent): void {
        const tachoColumnState = createTachoColumnState(event.api.getColumnState());
        dispatch(modifyLicenseTermsDisplayPreferences(tachoColumnState));
    }
    const onColumnResized = (event: ColumnResizedEvent) => {
        if (event.finished) {
            return onColumnsChanged(event);
        }
    };
    const onTableFiltersStateChanged = (event: FilterChangedEvent) => {
        const filters = event.api.getFilterModel();
        dispatch(modifyLicenseTermsTableFilters(filters));
    };

    return (
        <>
            <AgGridContextMenuWrapper gridApi={gridApi.current} showStatusBar>
                <AgGridReact
                    suppressPropertyNamesCheck
                    getRowId={params => params.data?.id ?? ''}
                    suppressDragLeaveHidesColumns
                    suppressContextMenu={true}
                    multiSortKey="ctrl"
                    rowSelection="multiple"
                    defaultColDef={licenseTermsTableDefaultColumnsDefs}
                    columnDefs={columnDefs}
                    modules={[ClientSideRowModelModule]}
                    onGridReady={onGridReady}
                    onSelectionChanged={rowsSelectionChanged}
                    suppressScrollOnNewData={true}
                    sideBar={sideBarConfiguration}
                    localeText={currentTranslations?.values}
                    onRowDoubleClicked={e => {
                        props.onDoubleClicked();
                    }}
                    components={{ tableButtonRenderer: TableButtonRenderer }}
                    onSortChanged={event => onColumnsChanged(event)}
                    onColumnMoved={event => onColumnsChanged(event)}
                    onColumnResized={event => onColumnResized(event)}
                    onFilterChanged={event => onTableFiltersStateChanged(event)}
                />
            </AgGridContextMenuWrapper>
        </>
    );
};
